/** Рассылка объявлений выбранным группам. */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  BROADCAST_PICK_LIMIT,
  tierLabel,
  TIERS,
  type PlayerCombat,
  type PlayerStats,
  type RosterEvent,
  type Tier,
} from '@ak/shared';

import { apiPost } from '../lib/api.ts';
import { canSendBroadcast } from '../lib/broadcast.ts';
import { formatCount } from '../lib/format.ts';
import { EMPTY_FILTERS, filterPlayers, type PlayerActivity, type PlayerFilters } from '../lib/player-filter.ts';
import { comparePlayers, type PlayerRow } from '../lib/player-sort.ts';
import { useResource } from '../lib/useResource.ts';
import { useToast } from '../components/toast.tsx';
import {
  Button,
  Checkbox,
  Field,
  FilterChip,
  Modal,
  PageHeader,
  Panel,
  SegmentedControl,
  Select,
  Skeleton,
  Spinner,
  TextArea,
  TextInput,
  TierBadge,
} from '../components/ui.tsx';

type AudienceKind = 'all' | 'event-main' | 'event-all' | 'tier' | 'players';

interface Preview {
  count: number;
  sample: string[];
}

/**
 * Предпросмотр вместе с аудиторией, для которой он посчитан.
 *
 * Без этой привязки была дыра: меняешь «Кому» — старое число ещё висит на
 * экране, кнопка «Отправить» активна, а отправка собирает аудиторию заново.
 * Один клик в это окно — и вместо троих объявление уходит шести сотням.
 */
interface KeyedPreview extends Preview {
  key: string;
}

const AUDIENCE_LABELS: Record<AudienceKind, string> = {
  all: 'Все с анкетой',
  'event-main': 'Основной состав набора',
  'event-all': 'Весь состав набора',
  tier: 'Игроки тира',
  players: 'Выбранные люди',
};

export function BroadcastPage(): ReactNode {
  const toast = useToast();
  const events = useResource<{ events: (RosterEvent & { plusCount: number })[] }>('/api/events?status=open,closed');

  const [kind, setKind] = useState<AudienceKind>('all');
  const [eventId, setEventId] = useState<number | null>(null);
  const [tier, setTier] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<KeyedPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const needsEvent = kind === 'event-main' || kind === 'event-all';

  useEffect(() => {
    if (needsEvent && eventId === null && events.data !== null && events.data.events.length > 0) {
      setEventId(events.data.events[0]?.id ?? null);
    }
  }, [needsEvent, eventId, events.data]);

  const audience = useMemo<Record<string, unknown>>(
    () => ({
      kind,
      ...(needsEvent ? { eventId } : {}),
      ...(kind === 'tier' ? { tier } : {}),
      ...(kind === 'players' ? { userIds: picked } : {}),
    }),
    [kind, needsEvent, eventId, tier, picked],
  );

  // Отпечаток аудитории: по нему видно, к тем ли людям относится показанное
  // число получателей.
  const audienceKey = JSON.stringify(audience);

  // Предпросмотр обновляется при смене аудитории: отправлять вслепую
  // сотне человек — плохая идея.
  useEffect(() => {
    setPreviewError(null);

    if ((needsEvent && eventId === null) || (kind === 'players' && picked.length === 0)) {
      setPreview(null);
      return;
    }

    /*
     * Ответ устаревшего запроса выбрасывается.
     *
     * Галочки щёлкают быстро, запрос уходит на каждую, и без этого медленный
     * ранний ответ лёг бы поверх свежего: на экране одно число получателей, в
     * отправку уходит другое. Для рассылки это не мелочь.
     */
    let cancelled = false;

    setPreviewing(true);
    apiPost<Preview>('/api/broadcast/preview', { ...audience, eventId, tier })
      .then((result) => {
        if (!cancelled) {
          setPreview({ ...result, key: audienceKey });
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return;
        }

        // Причину показываем, а не глотаем: отказ по пределу в 300 человек
        // приходил сюда же, и на экране оставалось «Выберите аудиторию» —
        // тупик, из которого не видно, что не так.
        setPreview(null);
        setPreviewError(cause instanceof Error ? cause.message : 'Не удалось посчитать получателей');
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [audience, audienceKey, kind, eventId, tier, needsEvent, picked]);

  const send = (): void => {
    setSending(true);
    setConfirming(false);
    apiPost<{ dm: { sent: number; targeted: number; failed: number } }>('/api/broadcast', { ...audience, message })
      .then((result) => {
        toast.success(
          `Доставлено ${String(result.dm.sent)} из ${String(result.dm.targeted)}` +
            (result.dm.failed > 0 ? `, не дошло ${String(result.dm.failed)}` : ''),
        );

        /*
         * Чистим не только текст, но и выбор.
         *
         * Раньше очищался один текст, форма выглядела свежей, а список
         * получателей оставался заряжен прошлой рассылкой: отметил ещё
         * одного человека — и объявление ушло ему вместе с теми четырьмя,
         * кому уже уходило.
         */
        setMessage('');
        setPicked([]);
        setPreview(null);
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось отправить');
      })
      .finally(() => {
        setSending(false);
      });
  };

  const previewMatches = preview !== null && preview.key === audienceKey;
  // Правило отправки вынесено и закрыто тестами: см. `lib/broadcast.ts`.
  const canSend = canSendBroadcast({
    message,
    previewKey: preview?.key ?? null,
    previewCount: preview?.count ?? 0,
    audienceKey,
    previewing,
    sending,
  });

  return (
    <>
      <PageHeader title="Рассылка" description="Объявление уходит в личные сообщения выбранной группе" />

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <Panel className="space-y-4 p-5">
          <Field label="Кому">
            <Select
              value={kind}
              onChange={(event) => {
                const next = event.target.value as AudienceKind;
                setKind(next);

                // Уходя с поимённого выбора, отметки сбрасываем: спрятанный
                // список получателей — это список, о котором забудут, а
                // вернувшись через час, отметят ещё двоих и отправят пятерым.
                if (next !== 'players') {
                  setPicked([]);
                }
              }}
            >
              {(Object.keys(AUDIENCE_LABELS) as AudienceKind[]).map((value) => (
                <option key={value} value={value}>
                  {AUDIENCE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>

          {needsEvent && (
            <Field label="Набор">
              <Select
                value={eventId === null ? '' : String(eventId)}
                onChange={(event) => {
                  setEventId(Number.parseInt(event.target.value, 10));
                }}
              >
                {(events.data?.events ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    #{item.id} · {item.eventType} · {item.status === 'open' ? 'открыт' : 'закрыт'}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {kind === 'players' && <PeoplePicker picked={picked} onChange={setPicked} />}

          {kind === 'tier' && (
            <Field label="Тир">
              <Select
                value={String(tier)}
                onChange={(event) => {
                  setTier(Number.parseInt(event.target.value, 10));
                }}
              >
                {TIERS.map((value) => (
                  <option key={value} value={String(value)}>
                    {tierLabel(value)}
                  </option>
                ))}
                <option value="0">без тира</option>
              </Select>
            </Field>
          )}

          <Field label="Текст объявления" hint="Разметка Discord работает: **жирный**, *курсив*, ссылки">
            <TextArea
              rows={8}
              value={message}
              maxLength={1800}
              placeholder="Сбор за 15 минут до начала в голосовом канале."
              onChange={(event) => {
                setMessage(event.target.value);
              }}
            />
          </Field>

          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {message.length} из 1800 символов
            </span>
            <Button
              variant="primary"
              busy={sending}
              disabled={!canSend}
              onClick={() => {
                setConfirming(true);
              }}
            >
              Отправить
            </Button>
          </div>
        </Panel>

        <Panel className="h-fit p-5">
          <h2 className="mb-3 font-semibold">Получатели</h2>

          {previewing || (preview !== null && !previewMatches) ? (
            <div style={{ color: 'var(--text-muted)' }}>
              <Spinner />
            </div>
          ) : previewError !== null ? (
            <p className="text-sm" style={{ color: 'var(--danger)' }}>
              {previewError}
            </p>
          ) : preview === null ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {kind === 'players' ? 'Отметьте, кому написать.' : 'Выберите аудиторию.'}
            </p>
          ) : (
            <>
              <div className="text-2xl font-semibold tabular-nums">
                {formatCount(preview.count, 'человек', 'человека', 'человек')}
              </div>
              {preview.sample.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {preview.sample.map((nick) => (
                    <li key={nick} className="truncate">
                      {nick}
                    </li>
                  ))}
                  {preview.count > preview.sample.length && (
                    <li style={{ color: 'var(--text-muted)' }}>…и ещё {preview.count - preview.sample.length}</li>
                  )}
                </ul>
              )}
              {preview.count === 0 && (
                <p className="mt-2 text-sm" style={{ color: 'var(--warning)' }}>
                  В этой группе никого нет.
                </p>
              )}
            </>
          )}

          <p className="mt-4 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            У кого закрыты личные сообщения, тот объявление не получит — такие попадут в отчёт как недоставленные.
          </p>
        </Panel>
      </div>

      {/*
        Подтверждение с числом и именами.
        Рассылку назад не вернуть, а число получателей и вид аудитории живут в
        разных углах экрана — перед необратимым их стоит показать вместе.
      */}
      {confirming && preview !== null && previewMatches && (
        <Modal
          title="Отправить объявление?"
          onClose={() => {
            setConfirming(false);
          }}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Отмена
              </Button>
              <Button variant="primary" onClick={send} busy={sending}>
                Отправить {formatCount(preview.count, 'человеку', 'людям', 'людям')}
              </Button>
            </>
          }
        >
          <p className="text-sm">
            Аудитория: <strong>{AUDIENCE_LABELS[kind]}</strong>. Получателей:{' '}
            <strong>{formatCount(preview.count, 'человек', 'человека', 'человек')}</strong>.
          </p>
          {preview.sample.length > 0 && (
            <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {preview.sample.join(', ')}
              {preview.count > preview.sample.length && ` и ещё ${String(preview.count - preview.sample.length)}`}
            </p>
          )}
          <p
            className="mt-3 rounded-xl px-3 py-2 text-sm whitespace-pre-wrap"
            style={{ background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
          >
            {message.trim()}
          </p>
        </Modal>
      )}
    </>
  );
}

/** Ответ списка игроков: из него и набирают получателей поимённо. */
interface PickerResponse {
  players: PlayerStats[];
  combat: Record<string, PlayerCombat>;
}

const ACTIVITY_OPTIONS: { value: PlayerActivity; label: string }[] = [
  { value: 'all', label: 'Любая' },
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'stale', label: 'Давно нет' },
];

/**
 * Подбор получателей поимённо.
 *
 * Отбор тот же самый, что в списке игроков (`filterPlayers`): написать
 * правила во второй раз значило бы их развести, и «давно не был» стало бы
 * означать здесь одно, а там другое.
 *
 * Отмеченные не сбрасываются при смене отбора. Набирают их обычно в
 * несколько заходов — сперва весь первый тир, потом двоих из третьего, — и
 * сброс на каждом шаге сделал бы поимённый выбор бесполезным. По этой же
 * причине отмеченные всегда видны: счётчик рядом с кнопкой показывает,
 * сколько их набралось за все заходы.
 */
function PeoplePicker({ picked, onChange }: { picked: string[]; onChange: (next: string[]) => void }): ReactNode {
  const { data, loading, error } = useResource<PickerResponse>('/api/players');
  // По умолчанию только те, кто на сервере: вышедшему личное сообщение
  // всё равно не доставить.
  const [filters, setFilters] = useState<PlayerFilters>({ ...EMPTY_FILTERS, status: 'present' });
  const [onlyPicked, setOnlyPicked] = useState(false);

  const chosen = useMemo(() => new Set(picked), [picked]);

  const rows = useMemo<PlayerRow[]>(
    () => (data?.players ?? []).map((player) => ({ ...player, combat: data?.combat[player.userId] ?? null })),
    [data],
  );

  /*
   * «Только отмеченные» — не украшение, а способ себя проверить.
   *
   * Людей набирают в несколько заходов и с разными фильтрами, отмеченные
   * расходятся по всему списку, а предпросмотр показывает лишь первую
   * дюжину имён. Без этого переключателя убедиться, кому именно уйдёт
   * объявление, было негде.
   */
  const shown = useMemo(() => {
    const matched = filterPlayers(rows, filters).sort((left, right) => comparePlayers(left, right, 'tier', true));

    return onlyPicked ? matched.filter((player) => chosen.has(player.userId)) : matched;
  }, [rows, filters, onlyPicked, chosen]);

  const shownPickedCount = shown.filter((player) => chosen.has(player.userId)).length;
  const allShownPicked = shown.length > 0 && shownPickedCount === shown.length;
  // Сколько ещё можно отметить: сервер больше предела не примет, и узнавать
  // об этом отказом после того, как набрал пять сотен, — плохо.
  const room = BROADCAST_PICK_LIMIT - picked.length;
  const tooManyToAdd = !allShownPicked && shown.length - shownPickedCount > room;

  const toggle = (userId: string): void => {
    onChange(chosen.has(userId) ? picked.filter((id) => id !== userId) : [...picked, userId]);
  };

  const patch = (next: Partial<PlayerFilters>): void => {
    setFilters((current) => ({ ...current, ...next }));
  };

  const toggleTier = (value: Tier): void => {
    patch({
      tiers: filters.tiers.includes(value) ? filters.tiers.filter((item) => item !== value) : [...filters.tiers, value],
    });
  };

  if (error !== null) {
    return (
      <p className="text-sm" style={{ color: 'var(--danger)' }}>
        Не удалось загрузить список игроков: {error}
      </p>
    );
  }

  return (
    <Field label="Кому именно" hint="Отбор помогает найти нужных, отмеченные остаются при его смене">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <TextInput
              placeholder="Поиск по нику, статику или имени в Discord"
              value={filters.query}
              onChange={(event) => {
                patch({ query: event.target.value });
              }}
            />
          </div>
          <SegmentedControl<PlayerActivity>
            label="Активность"
            value={filters.activity}
            onChange={(value) => {
              patch({ activity: value });
            }}
            options={ACTIVITY_OPTIONS}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {TIERS.map((value) => (
            <FilterChip
              key={value}
              label={tierLabel(value)}
              color={`var(--tier-${String(value)})`}
              active={filters.tiers.includes(value)}
              onSelect={() => {
                toggleTier(value);
              }}
            />
          ))}
          <FilterChip
            label="Без тира"
            color="var(--tier-none)"
            active={filters.tiers.includes(0)}
            onSelect={() => {
              toggleTier(0);
            }}
          />
          <FilterChip
            label="Без каптов"
            active={filters.problems.includes('noCaptures')}
            onSelect={() => {
              patch({
                problems: filters.problems.includes('noCaptures')
                  ? filters.problems.filter((item) => item !== 'noCaptures')
                  : [...filters.problems, 'noCaptures'],
              });
            }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button
            size="sm"
            disabled={shown.length === 0 || tooManyToAdd}
            title={
              tooManyToAdd
                ? `За раз можно выбрать не больше ${String(BROADCAST_PICK_LIMIT)} человек — сузьте отбор`
                : undefined
            }
            onClick={() => {
              const ids = shown.map((player) => player.userId);
              onChange(allShownPicked ? picked.filter((id) => !ids.includes(id)) : [...new Set([...picked, ...ids])]);
            }}
          >
            {allShownPicked ? 'Снять показанных' : `Выбрать показанных (${String(shown.length)})`}
          </Button>
          {picked.length > 0 && (
            <>
              <Button
                size="sm"
                variant={onlyPicked ? 'primary' : 'secondary'}
                onClick={() => {
                  setOnlyPicked((value) => !value);
                }}
              >
                Только отмеченные
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onChange([]);
                  setOnlyPicked(false);
                }}
              >
                Сбросить выбор
              </Button>
            </>
          )}
          <span className="ml-auto tabular-nums" style={{ color: 'var(--text-muted)' }}>
            отмечено {picked.length}
          </span>
        </div>

        {tooManyToAdd && (
          <p className="text-xs" style={{ color: 'var(--warning)' }}>
            За раз можно выбрать не больше {BROADCAST_PICK_LIMIT} человек. Сейчас отмечено {picked.length}, показано{' '}
            {shown.length} — сузьте отбор или отмечайте по одному.
          </p>
        )}

        {loading && data === null ? (
          <Skeleton rows={4} />
        ) : shown.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {onlyPicked ? 'Среди отмеченных под отбор никто не подходит.' : 'Под отбор никто не подходит.'}
          </p>
        ) : (
          <ul
            className="max-h-72 overflow-y-auto rounded-xl"
            style={{ border: '1px solid var(--border-subtle)' }}
          >
            {shown.map((player) => (
              <li
                key={player.userId}
                className="flex cursor-pointer items-center gap-2.5 border-b px-3 py-2 last:border-b-0"
                style={{ borderColor: 'var(--border-subtle)' }}
                onClick={() => {
                  toggle(player.userId);
                }}
              >
                <Checkbox
                  checked={chosen.has(player.userId)}
                  onChange={() => {
                    toggle(player.userId);
                  }}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{player.gameNick}</span>
                <TierBadge tier={player.tier} />
                <span className="shrink-0 text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                  {player.staticId}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}
